import { useMemo } from 'react'
import { api } from '../../api'
import { useCachedFetch } from '../../lib/swr'

/**
 * Canaux de vente du producteur — SOURCE UNIQUE (S69, refait S70).
 *
 * Deux retours d'Enzo le meme soir. D'abord : un canal ferme a son organisation
 * restait selectionnable dans « Où vendre ce produit ? » (trois copies de la
 * liste, une seule regardait les activations). Puis, capture a l'appui : « on
 * voit un module qui n'existe pas du tout, et ca propose les tournees alors que
 * ce module est desactive ». La liste etait CODEE EN DUR — Tournee, Evenements,
 * Atelier — et ne savait rien de ce que le BO decide.
 *
 * Ce que le BO decide, c'est `Module.portalState` (Admin → Modules) :
 *   MASQUE  = le lieu n'existe pas pour le portail → le canal N'APPARAIT PAS ;
 *   BIENTOT = visible mais pas ouvert → canal VISIBLE, grise, « Bientôt » ;
 *   ACTIF   = ouvert → selectionnable SI l'organisation a l'activation.
 * C'est la meme regle que l'Explorer client (MODULE-ACTIVATION-BRIEF, S20) :
 * un producteur ne peut pas vendre dans un lieu que le client ne voit pas.
 *
 * Deux sources, croisees ici et nulle part ailleurs :
 *   - `GET /public/modules` (lieux ≠ MASQUE, avec leur etat, cache SWR
 *     persistant deja utilise par l'Explorer — aucun appel de plus) ;
 *   - `org.moduleActivations` (activations ACTIF sur un module actif ET ouvert,
 *     filtrees par le backend, `findOrgWithDetails`).
 * Le backend applique la meme regle a l'ecriture (`createMyOffer`,
 * `setOfferChannel`) : la regle est portee par le code des deux cotes.
 */

// Habillage par code (prototype MODULE_THEME). `tarife` : porte une commission
// (l'Atelier n'est pas tarife — « tarification a venir », prototype).
const HABILLAGE = {
  // `l` : nom de repli quand le BO ne fournit pas de libelle (lieu masque) —
  // Enzo (S72) : « on va l'appeler Paniers, pas Tournee ».
  TOURNEE: { c: 'rgb(var(--c-primary))', e: '🧺', l: 'Paniers', d: 'Livraison de paniers', tarife: true },
  MARCHE: { c: 'rgb(var(--c-teal))', e: '🎪', l: 'Évènements', d: 'Marchés, festivals, villages', tarife: true },
  TRANSFORMATION: { c: '#d99a2b', e: '🧃', l: 'Atelier', d: 'Transformation', tarife: false },
}
const HABILLAGE_DEFAUT = { c: 'rgb(var(--c-teal))', e: '📦', d: '', tarife: true }

export const RAISON_CANAL_FERME = 'Canal non activé — demandez-le à Manrina' // repli d'un canal principal ferme (EditOfferModal)
export const RAISON_CANAL_BIENTOT = 'Bientôt disponible'

/** Activation de l'organisation pour ce code (objet `module`), ou null. */
export const moduleDuCanal = (modules, code) =>
  (modules || []).find((m) => m?.module?.code === code)?.module || null

/**
 * Catalogue des canaux = lieux du portail × activations de l'organisation.
 * Chaque canal : { code, l, e, c, d, tarife, etat, ouvert, module, raison }.
 * `lieux` absent (hors ligne, avant la toute premiere reponse) : repli sur les
 * activations seules — elles sont deja filtrees sur les modules ouverts.
 */
export function construireCanaux(lieux, modules) {
  const source = Array.isArray(lieux)
    ? lieux
    : (modules || []).map((m) => ({ code: m.module.code, nom: m.module.nom, state: 'ACTIF' }))
  // Enzo (S72) : un module NON ACTIVE pour l'organisation n'apparait pas, meme
  // grise — seul « Bientot » (lieu ouvert au portail, pas encore en service)
  // reste visible, eteint. Un canal ferme a l'organisation n'existe pas pour elle.
  return catalogueBrut(source, modules).filter((c) => c.etat !== 'FERME')
}

function catalogueBrut(source, modules) {
  return source.map((lieu) => {
    const hab = HABILLAGE[lieu.code] || HABILLAGE_DEFAUT
    const module = moduleDuCanal(modules, lieu.code)
    const etat = lieu.state === 'BIENTOT' ? 'BIENTOT' : module ? 'OUVERT' : 'FERME'
    return {
      code: lieu.code,
      l: lieu.nom,
      e: HABILLAGE[lieu.code]?.e || lieu.emoji || HABILLAGE_DEFAUT.e,
      c: hab.c,
      d: hab.d || lieu.description || '',
      tarife: hab.tarife,
      etat,
      ouvert: etat === 'OUVERT',
      module,
      raison: etat === 'BIENTOT' ? RAISON_CANAL_BIENTOT : etat === 'FERME' ? RAISON_CANAL_FERME : null,
    }
  })
}

/** Le catalogue des canaux pour cette organisation (`modules` = ses activations). */
export function useCanaux(modules) {
  const { data } = useCachedFetch('/public/modules', () => api.getModules(), { persist: true })
  return useMemo(() => construireCanaux(data, modules), [data, modules])
}

/**
 * Peut-on basculer ce canal sur une offre ? Un canal non ouvert ne peut plus
 * etre ACTIVE, mais un canal deja actif sur l'offre doit pouvoir etre COUPE —
 * sinon l'offre resterait vendue sur un canal que Manrina a retire.
 */
export const basculePossible = (canal, actuellementActif) => actuellementActif || !!canal?.ouvert

/** Canaux coches ET ouverts (ceux qui compteront a la creation). */
export const canauxChoisis = (catalogue, canaux) => catalogue.filter((ch) => ch.ouvert && canaux?.[ch.code])

/** Canaux choisis qui portent une commission (donc un prix a saisir). */
export const canauxTarifes = (catalogue, canaux) => canauxChoisis(catalogue, canaux).filter((ch) => ch.tarife)

/** Prototype : la Tournee est cochee d'office. Ici : le PREMIER canal ouvert. */
export const canauxInitiaux = (catalogue) => {
  const premier = catalogue.find((ch) => ch.ouvert)
  return premier ? { [premier.code]: true } : {}
}

/**
 * Canal principal d'une offre (son module) tel qu'il faut l'afficher, meme si le
 * lieu a ete masque depuis : on ne fait pas disparaitre ce sur quoi l'offre est
 * deja vendue.
 */
export const canalPrincipalDeLOffre = (catalogue, offer) => {
  const code = offer?.module?.code
  if (!code) return null
  return catalogue.find((ch) => ch.code === code) || {
    code, l: HABILLAGE[code]?.l || offer.module.nom || code, e: HABILLAGE[code]?.e || HABILLAGE_DEFAUT.e,
    c: HABILLAGE[code]?.c || HABILLAGE_DEFAUT.c, d: '', tarife: true, etat: 'FERME', ouvert: false, module: null, raison: null,
  }
}

/**
 * Canaux TARIFES et OUVERTS d'une offre, avec leur taux (0-1) et leur prix de
 * vente client — ce que la feuille ✏️ affiche et ecrit, et ce que « Vous
 * recevez » (Mes offres) resume. Extrait d'EditOfferModal (S75) pour que les
 * deux ecrans lisent LE MEME prix : avant, « Vous recevez » lisait le prix de
 * base (canal principal, parfois Paniers ferme) quand la feuille ecrivait le
 * prix du canal Evenements (`prixCanal`).
 *
 * Canal Evenements : le taux est celui du MARCHE (module, 10 % par defaut —
 * c'est lui que regle le producteur, ADR-058 §5), jamais le `commissionRate`
 * de l'offre (30 % par defaut au BO).
 */
export const canauxTarifesDeLOffre = (catalogue, offer) => {
  const tauxDe = (code, stocke, canal) => Number(
    code === 'MARCHE' ? (canal?.module?.commissionDefaut ?? 10) : (stocke ?? canal?.module?.commissionDefaut ?? 10),
  ) / 100
  const principal = canalPrincipalDeLOffre(catalogue || [], offer)
  const canaux = []
  if (principal && principal.ouvert) {
    canaux.push({ ...principal, principal: true, taux: tauxDe(principal.code, offer?.commissionRate, principal), prix: Number(offer?.prix || 0) })
  }
  for (const ch of offer?.channels || []) {
    const code = ch.module?.code
    if (!ch.isActive || !code || code === principal?.code) continue
    const canal = (catalogue || []).find((c) => c.code === code)
    if (!canal?.ouvert || !canal.tarife) continue
    canaux.push({
      ...canal, principal: false,
      taux: tauxDe(code, ch.commissionRate ?? offer?.commissionRate, canal),
      prix: Number(ch.prixCanal ?? offer?.prix ?? 0),
    })
  }
  return canaux.filter((c) => c.tarife !== false)
}

/** Net du producteur sur un canal : prix client x (1 - taux), au centime. */
export const netDuCanal = (c) => Math.round(c.prix * (1 - c.taux) * 100) / 100

